import {
    useEffect, useState, useRef, forwardRef, useImperativeHandle,
} from 'react';
import {
    Button, Space, Modal, Row, Col, FormInstance,
    Alert,
} from 'antd';
import { useTranslation } from 'react-i18next';
import produce from 'immer';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useOrder } from '../../hooks/useOrder';
import Table from '../table';
import OrderItemForm, { OrderItemFormRef, OrderItemFormValues } from './item-form';
import { useFormWatchMode } from '../../hooks/useFormWatchMode';
import { useFetch } from '../../hooks/useFetch';
import { USER_ROLE_MODERATOR_LIMITED, useUser } from '../../hooks/useUser';

export interface IOrderItemsRef {
    getItems: () => OrderItemFormValues[];
}

interface IOrderItemsProps {
    orderForm: FormInstance;
}

const OrderItems = forwardRef(({ orderForm }: IOrderItemsProps, ref) => {
    const fetch = useFetch();
    const order = useOrder();
    const { i18n, t } = useTranslation();
    const [dataSource, setDataSource] = useState<OrderItemFormValues[]>([]);
    const [dutyMessage, setDutyMessage] = useState<string>();
    const form = useRef<OrderItemFormRef>();
    const user = useUser({ canFetch: false, canLogoutBy401: false, showFetchMessages: false });
    const { watchMode } = useFormWatchMode();

    const limitedAccess = user.info?.role_value === USER_ROLE_MODERATOR_LIMITED;

    const sum = dataSource.reduce((val, row) => {
        val += typeof row.cost === 'number' ? row.cost : parseFloat(row.cost) || 0;
        return val;
    }, 0);

    const actions = (_: any, row: OrderItemFormValues) => (
        <Space>
            <Button icon={<EditOutlined />} type="link" onClick={() => onEditClick(row)} />
            {!limitedAccess && <Button icon={<DeleteOutlined />} type="link" onClick={() => onDeleteClick(row)} />}
        </Space>
    );

    const getIndex = (row: OrderItemFormValues) => {
        if (typeof row.id === 'number') {
            return dataSource.findIndex((el) => el.id === row.id);
        }
        if (typeof row.newRowId === 'number') {
            return dataSource.findIndex((el) => el.newRowId === row.newRowId);
        }
        return -1;
    };

    const onEditClick = (row: OrderItemFormValues) => {
        if (form.current) {
            const index = getIndex(row);
            if (index > -1) {
                form.current.show(row, index);
            }
        }
    };

    const onDeleteClick = (row: OrderItemFormValues) => {
        Modal.confirm({
            title: t('confirmAction'),
            content: t('deleteConfirmation'),
            onOk: () => {
                const index = getIndex(row);
                if (index > -1) {
                    setDataSource(produce(dataSource, (draft) => {
                        draft?.splice(index, 1);
                    }));
                }
            },
            okText: t('delete'),
        });
    };

    const onFormSubmit = (values: OrderItemFormValues) => {
        const { index } = values;
        if (typeof index === 'undefined') {
            setDataSource(produce(dataSource, (draft) => {
                draft.unshift(values);
            }));
        } else if (typeof index !== 'undefined') {
            setDataSource(produce(dataSource, (draft) => {
                if (draft[index]) {
                    draft[index] = values;
                }
            }));
        }
    };

    const onPlusClick = () => {
        if (form.current) {
            form.current.show();
        }
    };

    const validateDutySum = async () => {
        if (sum > 0) {
            const response = await fetch.request({
                url: '/orders/validate-duty-sum',
                method: 'post',
                data: {
                    from: orderForm.getFieldValue('from'),
                    sum,
                },
            });
            setDutyMessage(response.success ? response.message : undefined);
        } else {
            setDutyMessage(undefined);
        }
    };

    useEffect(() => {
        setDataSource(order.data?.package?.items || []);
    }, [order.data]);

    useEffect(() => {
        validateDutySum();
    }, [i18n.language, sum]);

    useImperativeHandle(ref, () => ({
        getItems: () => dataSource || [],
    }));

    const totalSum = () => (
        <span>
            {`${t('sum')}: ${sum}€`}
        </span>
    );

    return (
        <Row gutter={[30, 30]}>
            <OrderItemForm ref={form} orderForm={orderForm} onSubmit={onFormSubmit} />
            {!watchMode && !limitedAccess && (
                <Col xs={24}>
                    <Button type="primary" onClick={onPlusClick}>
                        Заполнить данные
                    </Button>
                </Col>
            )}
            <Col xs={24}>
                <Table
                    scroll={{ x: true }}
                    dataSource={dataSource}
                    bordered
                    columns={[{
                        align: 'center',
                        dataIndex: 'name',
                        title: t('orderPackageName'),
                    }, {
                        align: 'center',
                        dataIndex: 'count',
                        title: t('orderPackageItemsCount'),
                    }, {
                        align: 'center',
                        dataIndex: 'cost',
                        title: t('orderPackageCost'),
                    }, ...(watchMode ? [] : [{
                        align: 'center',
                        dataIndex: '',
                        render: actions,
                        width: 100,
                    } as any])]}
                    pagination={{
                        pageSize: dataSource.length,
                        showTotal: totalSum,
                    }}
                />
                {typeof dutyMessage === 'string' && (
                    <Alert className="order-items-sum-alert" message={dutyMessage} />
                )}
            </Col>
        </Row>
    );
});

export default OrderItems;
